import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button, Icon, Input, Spin, message } from 'antd'
import ReactPlayer from 'react-player'
import { useVideoEditor } from '../../hooks/useVideoEditor'

const { TextArea } = Input

const COLORS = {
  primary: '#5057d5',
  primaryLight: '#e8f0fe',
  surface: '#ffffff',
  background: '#f8f9fa',
  text: '#202124',
  textSecondary: '#5f6368',
  textMuted: '#80868b',
  border: '#dadce0',
  borderLight: '#e8eaed',
  success: '#1e8e3e',
  error: '#d93025',
  white: '#ffffff'
}

const VideoEditorModal = ({
  visible,
  onCancel,
  onConfirm,
  initialPrompt = '',
  currentVideo = null,
  persistedConversation = [],
  activeTaskId = null,
  onUpdateState = () => {},
  onCancelVideoEditing,
  videoId
}) => {
  const [prompt, setPrompt] = useState('')
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false)
  const chatEndRef = useRef(null)

  const {
    conversation,
    isGenerating,
    selectedReference,
    handleSend,
    handleCancel,
    handleClear
  } = useVideoEditor({
    persistedConversation,
    activeTaskId,
    initialReference: currentVideo,
    onUpdateState,
    videoId
  })

  const scrollToBottom = () => {
    chatEndRef.current &&
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [conversation])

  useEffect(() => {
    if (visible && initialPrompt && conversation.length === 0) {
      setPrompt(initialPrompt)
    }
  }, [visible, initialPrompt, conversation.length])

  const onSend = async () => {
    if (!prompt.trim()) return
    await handleSend(prompt)
    setPrompt('')
  }

  const handleUseVersion = url => {
    Modal.confirm({
      title: 'Use this version?',
      content:
        'This will set this video as the current version for this shot and save it to history.',
      okText: 'Yes, Use it',
      centered: true,
      cancelText: 'Cancel',
      okButtonProps: {
        style: { background: COLORS.primary, borderColor: COLORS.primary }
      },
      onOk: () => {
        onConfirm(url)
      }
    })
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSend()
    }
  }

  const handleCancelClick = () => {
    setIsCancelModalVisible(true)
  }

  return (
    <Modal
      visible={visible}
      title={null}
      centered
      onCancel={onCancel}
      width={800} // Narrower since sidebar is gone
      maskClosable={false}
      bodyStyle={{
        padding: 0,
        height: '80vh',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '12px',
        overflow: 'hidden'
      }}
      footer={null}
    >
      {/* Header */}
      <div
        style={{
          padding: '16px 24px',
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: COLORS.white
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 18, color: COLORS.text }}>
            Video AI Editor
          </div>
          <p style={{ fontSize: 13, color: COLORS.textMuted, margin: 0 }}>
            Describe changes and iterate on your shots
          </p>
        </div>
        <Button
          type="link"
          size="small"
          onClick={handleClear}
          disabled={isGenerating || conversation.length === 0}
          style={{
            color: COLORS.textSecondary,
            display: 'flex',
            alignItems: 'center',
            gap: 4
          }}
        >
          <Icon type="delete" /> Clear Chat
        </Button>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Chat Area */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            background: '#fafafa',
            padding: '24px'
          }}
        >
          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0 8px 16px 8px'
            }}
          >
            {conversation.length === 0 && (
              <div
                style={{
                  marginBottom: 24,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
                {selectedReference ? (
                  <div
                    style={{
                      width: '100%',
                      maxWidth: 400,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 12
                    }}
                  >
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: COLORS.text,
                        textAlign: 'center',
                        background: COLORS.primaryLight,
                        padding: '8px 16px',
                        borderRadius: 20,
                        width: 'fit-content',
                        margin: '0 auto'
                      }}
                    >
                      Base Video
                    </div>
                    <div
                      style={{
                        background: COLORS.white,
                        padding: 8,
                        borderRadius: 12,
                        border: `1px solid ${COLORS.border}`,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          aspectRatio: '16/9',
                          background: '#000',
                          borderRadius: 8,
                          overflow: 'hidden'
                        }}
                      >
                        <ReactPlayer
                          url={selectedReference}
                          width="100%"
                          height="100%"
                          controls
                        />
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          padding: '4px 4px 0 4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          borderTop: `1px solid ${COLORS.borderLight}`
                        }}
                      >
                        {selectedReference && (
                          <div
                            style={{
                              fontSize: 11,
                              color: COLORS.textSecondary,
                              fontStyle: 'italic',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                          >
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: COLORS.success
                              }}
                            />
                            Active Reference
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: 'center',
                        color: COLORS.textSecondary,
                        fontSize: 13,
                        marginTop: 12
                      }}
                    >
                      Describe the changes you want to make to this video below.
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      color: COLORS.textMuted,
                      textAlign: 'center',
                      paddingTop: '20%'
                    }}
                  >
                    <div
                      style={{
                        height: 56,
                        width: 56,
                        background: COLORS.primaryLight,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 16
                      }}
                    >
                      <Icon
                        type="video-camera"
                        style={{ color: COLORS.primary, fontSize: 24 }}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: COLORS.text,
                        marginBottom: 8
                      }}
                    >
                      Start Editing Your Video
                    </div>
                    <div style={{ maxWidth: 300 }}>
                      Describe the changes you want to make to the current
                      video.
                    </div>
                  </div>
                )}
              </div>
            )}

            {conversation.map((msg, idx) => (
              <div key={idx} style={{ marginBottom: 24 }}>
                {/* User Message */}
                {msg.type === 'user' && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginBottom: 12
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '70%',
                        background: COLORS.primary,
                        padding: '12px 18px',
                        borderRadius: '18px 18px 4px 18px',
                        color: COLORS.white,
                        boxShadow: '0 2px 8px rgba(80, 87, 213, 0.2)'
                      }}
                    >
                      <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                        {msg.content}
                      </div>
                      {msg.reference && (
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 11,
                            opacity: 0.8,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            wordBreak: 'break-all',
                            maxWidth: '100%'
                          }}
                        >
                          <Icon type="link" style={{ fontSize: 10 }} />
                          Reference:{' '}
                          {
                            msg.reference
                              .split('/')
                              .pop()
                              .split('?')[0]
                          }
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Loading */}
                {msg.type === 'loading' && (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <div
                      style={{
                        background: COLORS.white,
                        padding: '12px 20px',
                        borderRadius: '4px 18px 18px 18px',
                        border: `1px solid ${COLORS.borderLight}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                      }}
                    >
                      <Spin size="small" />
                      <span
                        style={{ color: COLORS.textSecondary, fontSize: 14 }}
                      >
                        AI is processing your video. This may take a few
                        minutes...
                      </span>
                      <Button
                        type="link"
                        size="small"
                        onClick={handleCancelClick}
                        style={{
                          color: COLORS.error,
                          marginLeft: 8,
                          padding: '0 4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <Icon type="close-circle" /> Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Assistant Response */}
                {msg.type === 'assistant' && (
                  <div
                    style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                  >
                    <div
                      style={{
                        background: COLORS.white,
                        padding: 8,
                        borderRadius: '4px 18px 18px 18px',
                        border: `1px solid ${COLORS.border}`,
                        width: 'fit-content',
                        maxWidth: '85%',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
                      }}
                    >
                      <div
                        style={{
                          width: 320,
                          aspectRatio: '16/9',
                          background: '#000',
                          borderRadius: 12,
                          overflow: 'hidden'
                        }}
                      >
                        <ReactPlayer
                          url={msg.content}
                          width="100%"
                          height="100%"
                          controls
                        />
                      </div>
                      <div
                        style={{
                          marginTop: 8,
                          padding: '4px 4px 0 4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          borderTop: `1px solid ${COLORS.borderLight}`
                        }}
                      >
                        <Button
                          type="link"
                          size="small"
                          onClick={() => handleUseVersion(msg.content)}
                          style={{
                            color: COLORS.primary,
                            fontWeight: 600,
                            padding: 0
                          }}
                        >
                          <Icon type="check" /> Use This Version
                        </Button>

                        {selectedReference === msg.content && (
                          <div
                            style={{
                              fontSize: 11,
                              color: COLORS.textSecondary,
                              fontStyle: 'italic',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4
                            }}
                          >
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: COLORS.success
                              }}
                            />
                            Active Reference
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Error */}
                {msg.type === 'error' && (
                  <div
                    style={{
                      background: '#fff1f0',
                      padding: '12px 18px',
                      borderRadius: '4px 18px 18px 18px',
                      border: '1px solid #ffa39e',
                      color: COLORS.error,
                      fontSize: 14,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <Icon type="close-circle" theme="filled" />
                    {msg.content}
                  </div>
                )}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div
            style={{
              padding: '16px 0 0 0',
              background: 'transparent'
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'flex-end',
                background: COLORS.white,
                padding: '8px 12px',
                borderRadius: 12,
                border: `1px solid ${COLORS.border}`,
                boxShadow: '0 2px 12px rgba(0,0,0,0.05)'
              }}
            >
              <TextArea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Describe how you want to edit the video..."
                autoSize={{ minRows: 1, maxRows: 5 }}
                disabled={isGenerating}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  padding: '8px 4px',
                  resize: 'none',
                  fontSize: 14
                }}
              />
              <Button
                type="primary"
                shape="circle"
                icon="arrow-up"
                onClick={onSend}
                loading={isGenerating}
                disabled={!prompt.trim() || !selectedReference}
                style={{
                  height: 36,
                  width: 36,
                  minWidth: 36,
                  background: COLORS.primary,
                  borderColor: COLORS.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Cancellation Confirmation Modal */}
      <Modal
        visible={isCancelModalVisible}
        onCancel={() => setIsCancelModalVisible(false)}
        footer={null}
        centered
        width={400}
        bodyStyle={{
          padding: '32px 24px',
          textAlign: 'center',
          borderRadius: '16px'
        }}
      >
        <div
          style={{
            height: 56,
            width: 56,
            background: '#fff1f0',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px auto'
          }}
        >
          <Icon
            type="warning"
            theme="filled"
            style={{ color: COLORS.error, fontSize: 24 }}
          />
        </div>
        <h3
          style={{
            fontSize: 20,
            fontWeight: 600,
            color: COLORS.text,
            marginBottom: 12
          }}
        >
          Cancel AI Editing?
        </h3>
        <p
          style={{
            fontSize: 14,
            color: COLORS.textSecondary,
            lineHeight: 1.6,
            marginBottom: 32
          }}
        >
          This will stop the current AI video generation process. You can start
          a new edit after cancelling.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Button
            type="primary"
            size="large"
            onClick={() => {
              handleCancel()
              onCancelVideoEditing()
              setIsCancelModalVisible(false)
            }}
            style={{
              background: COLORS.error,
              borderColor: COLORS.error,
              height: 48,
              borderRadius: 12,
              fontWeight: 600
            }}
          >
            Yes, Cancel it
          </Button>
          <Button
            type="link"
            size="large"
            onClick={() => setIsCancelModalVisible(false)}
            style={{ color: COLORS.textMuted, fontWeight: 500 }}
          >
            No, Keep waiting
          </Button>
        </div>
      </Modal>
    </Modal>
  )
}

export default VideoEditorModal
